import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase, ROLES } from "../../config/supabase";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { sendEditedVideoNotification } from "../../utils/gameResultNotifications";
import { getContentDisplayTitle } from "../../utils/contentDisplayTitles";
import {
	Box,
	Typography,
	Button,
	Grid,
	IconButton,
	Paper,
	Alert,
	Stack,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TableContainer,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Chip,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Autocomplete,
	Collapse,
	Skeleton,
} from "@mui/material";
import {
	Download,
	Upload,
	Calendar,
	School,
	User,
	GamepadIcon,
	Edit,
	Search,
	ChevronDown,
	ChevronUp,
	Image,
	Video,
	Trash,
	Eye,
} from "lucide-react";

const GAME_RESULTS_PAGE_SIZE = 1000;
const RELATED_DATA_CHUNK_SIZE = 100;

const chunkArray = (items = [], size = RELATED_DATA_CHUNK_SIZE) => {
	const chunks = [];
	for (let i = 0; i < items.length; i += size) {
		chunks.push(items.slice(i, i + size));
	}
	return chunks;
};

const fetchRowsByIdsInChunks = async (table, select, column, ids) => {
	if (!ids.length) return [];

	let allRows = [];
	for (const idChunk of chunkArray(ids)) {
		const { data, error } = await supabase
			.from(table)
			.select(select)
			.in(column, idChunk);

		if (error) throw error;
		allRows = allRows.concat(data || []);
	}

	return allRows;
};

const fetchAllGameResultsForYear = async (academicYear) => {
	let allRows = [];
	let from = 0;

	while (true) {
		const { data, error } = await supabase
			.from("game_results")
			.select("*")
			.eq("academic_year", academicYear)
			.order("created_at", { ascending: false })
			.range(from, from + GAME_RESULTS_PAGE_SIZE - 1);

		if (error) throw error;

		const batch = data || [];
		allRows = allRows.concat(batch);

		if (batch.length < GAME_RESULTS_PAGE_SIZE) break;
		from += GAME_RESULTS_PAGE_SIZE;
	}

	return allRows;
};

const isStorageObjectMissing = (error) => {
	if (!error) return false;
	const status = String(error.statusCode ?? error.status ?? "");
	const message = String(error.message ?? error.error ?? "").toLowerCase();
	return (
		status === "404" ||
		message.includes("not found") ||
		message.includes("object not found")
	);
};

const getStorageActionErrorMessage = (error, actionLabel) => {
	if (isStorageObjectMissing(error)) {
		return "File not found in storage. The record exists but the file is missing — ask the teacher to re-upload.";
	}
	return `Unable to ${actionLabel}. Please try again.`;
};

const GameResultsEdits = () => {
	const { user } = useAuth();
	const {
		selectedAcademicYear: selectedYear,
		setSelectedAcademicYear: setSelectedYear,
		currentAcademicYear,
	} = useSelectedAcademicYear();

	const [gameResults, setGameResults] = useState([]);
	const [filteredResults, setFilteredResults] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// Role and access control states
	const [userProfile, setUserProfile] = useState(null);
	const [userSchoolIds, setUserSchoolIds] = useState([]);

	// Filter states
	const [searchQuery, setSearchQuery] = useState("");
	const [schoolFilter, setSchoolFilter] = useState(null);
	const [gradeFilter, setGradeFilter] = useState(null);
	const [gameFilter, setGameFilter] = useState(null);
	const [editedFilter, setEditedFilter] = useState("all");
	const [showFilters, setShowFilters] = useState(false);

	// Filter options
	const [schools, setSchools] = useState([]);
	const [grades, setGrades] = useState([]);
	const [games, setGames] = useState([]);

	// Upload dialog state
	const [uploadDialog, setUploadDialog] = useState(false);
	const [selectedResult, setSelectedResult] = useState(null);
	const [uploadingEdit, setUploadingEdit] = useState(false);
	const [editFile, setEditFile] = useState(null);

	const [deleteDialog, setDeleteDialog] = useState(false);
	const [resultToDelete, setResultToDelete] = useState(null);
	const [deletingResult, setDeletingResult] = useState(false);

	const isAdmin = useMemo(
		() => userProfile?.role === "ROLE_FUNMIYO_ADMIN",
		[userProfile?.role]
	);

	const canDelete = useMemo(
		() =>
			userProfile?.role === ROLES.FUNMIYO_ADMIN ||
			userProfile?.role === ROLES.VIDEO_EDITOR ||
			userProfile?.role === ROLES.FUNMATH_ADMIN,
		[userProfile?.role]
	);

	const handleDeleteResult = useCallback(async () => {
		if (!resultToDelete) return;

		setDeletingResult(true);
		try {
			// First, delete the files from storage
			const deletePromises = [];

			// Delete original file
			if (resultToDelete.file_path) {
				deletePromises.push(
					supabase.storage
						.from("game-results")
						.remove([resultToDelete.file_path])
				);
			}

			// Delete edited file if exists
			if (resultToDelete.edited_file_path) {
				deletePromises.push(
					supabase.storage
						.from("game-results")
						.remove([resultToDelete.edited_file_path])
				);
			}

			// Execute storage deletions (don't fail if files don't exist)
			await Promise.allSettled(deletePromises);

			// Clear WhatsApp job references so FK does not block delete
			const { error: whatsappClearError } = await supabase
				.from("whatsapp_upload_jobs")
				.update({ game_result_id: null })
				.eq("game_result_id", resultToDelete.id);

			if (whatsappClearError) {
				console.warn(
					"Could not clear WhatsApp job references:",
					whatsappClearError
				);
			}

			// Delete download tracking records first (FK child rows)
			const { error: downloadDeleteError } = await supabase
				.from("game_result_downloads")
				.delete()
				.eq("game_result_id", resultToDelete.id);

			if (downloadDeleteError) {
				throw new Error(
					`Could not delete download records: ${downloadDeleteError.message}`
				);
			}

			// Delete the main game result record
			const { data: deletedRows, error: resultDeleteError } = await supabase
				.from("game_results")
				.delete()
				.eq("id", resultToDelete.id)
				.select("id");

			if (resultDeleteError) {
				throw resultDeleteError;
			}

			if (!deletedRows?.length) {
				throw new Error(
					"Delete was blocked. You may not have permission to delete this record."
				);
			}

			// Update local state by removing the deleted item
			const updateResults = (results) =>
				results.filter((result) => result.id !== resultToDelete.id);

			setGameResults((prev) => {
				const updated = updateResults(prev);
				setFilteredResults((current) => updateResults(current));
				return updated;
			});

			setDeleteDialog(false);
			setResultToDelete(null);
			setError("Game result deleted successfully!");

			// Auto-clear success message after 5 seconds
			setTimeout(() => setError(""), 5000);
		} catch (error) {
			console.error("Error deleting game result:", error);
			setError(
				error?.message ||
					"Failed to delete game result. Please try again."
			);
		} finally {
			setDeletingResult(false);
		}
	}, [resultToDelete]);

	// MEMOIZED: Define roles that require school filtering
	const SCHOOL_FILTERED_ROLES = useMemo(
		() => ["ROLE_FUNMATH_ADMIN", "ROLE_FUNMIYO_EMPLOYEE"],
		[]
	);

	// MEMOIZED: Check if user role requires school filtering
	const requiresSchoolFiltering = useMemo(
		() => userProfile?.role && SCHOOL_FILTERED_ROLES.includes(userProfile.role),
		[userProfile?.role, SCHOOL_FILTERED_ROLES]
	);

	// MEMOIZED: Helper functions
	const getFileTypeAndExtension = useCallback((file) => {
		if (!file.original_filename && !file.file_path)
			return { type: "unknown", extension: "" };

		const filename = file.original_filename || file.file_path.split("/").pop();
		const extension = filename.split(".").pop().toLowerCase();

		let type = "unknown";
		if (file.file_type) {
			type = file.file_type;
		} else {
			const imageExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
			const videoExts = ["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"];

			if (imageExts.includes(extension)) {
				type = "image";
			} else if (videoExts.includes(extension)) {
				type = "video";
			}
		}

		return { type, extension };
	}, []);

	const getEditedFileTypeAndExtension = useCallback((file) => {
		if (!file.edited_file_type) return { type: "unknown", extension: "" };

		if (file.edited_file_type.includes(":")) {
			const [type, extension] = file.edited_file_type
				.split(":")
				.map((s) => s.trim());
			return { type, extension };
		}

		if (file.edited_file_path) {
			const extension = file.edited_file_path.split(".").pop().toLowerCase();
			return { type: file.edited_file_type, extension };
		}

		return { type: file.edited_file_type, extension: "" };
	}, []);

	// MEMOIZED: Check user role function
	const checkUserRole = useCallback(async () => {
		try {
			if (!user?.id) return;

			const { data, error } = await supabase
				.from("profiles")
				.select("id, role, full_name, email")
				.eq("id", user.id)
				.single();

			if (error) throw error;
			setUserProfile(data);
		} catch (error) {
			console.error("Error checking user role:", error);
			setError("Error checking user permissions");
			setLoading(false);
		}
	}, [user?.id]);

	// MEMOIZED: Fetch user schools function
	const fetchUserSchools = useCallback(async () => {
		try {
			if (!user?.id) return;

			const [employeeSchools, visitSchools] = await Promise.all([
				supabase
					.from("employee_schools")
					.select("school_id")
					.eq("profile_id", user.id)
					.eq("active_status", true),
				supabase
					.from("school_visits")
					.select("school_id")
					.or(`planned_by.eq.${user.id},visited_by.eq.${user.id}`),
			]);

			if (employeeSchools.error) throw employeeSchools.error;
			if (visitSchools.error) throw visitSchools.error;

			const employeeSchoolIds =
				employeeSchools.data?.map((es) => es.school_id) || [];
			const visitSchoolIds = visitSchools.data?.map((vs) => vs.school_id) || [];
			const allSchoolIds = [
				...new Set([...employeeSchoolIds, ...visitSchoolIds]),
			].filter(Boolean);

			setUserSchoolIds(allSchoolIds);
		} catch (error) {
			console.error("Error fetching user schools:", error);
			setError("Error fetching assigned schools");
			setLoading(false);
		}
	}, [user?.id]);

	// MEMOIZED: Fetch game results function
	const fetchGameResults = useCallback(
		async (schoolIds = []) => {
			if (!selectedYear) return;

			setLoading(true);
			try {
				const gameResultsData = await fetchAllGameResultsForYear(selectedYear);

				// Get unique IDs for related data
				const teacherIds = [
					...new Set(gameResultsData.map((r) => r.teacher_id).filter(Boolean)),
				];
				const categoryIds = [
					...new Set(gameResultsData.map((r) => r.category_id).filter(Boolean)),
				];
				const contentIds = [
					...new Set(gameResultsData.map((r) => r.content_id).filter(Boolean)),
				];

				// Fetch related data in parallel (chunked .in() to avoid URL length limits)
				const [profilesRows, categoriesRows, contentsRows, gradesData] =
					await Promise.all([
						fetchRowsByIdsInChunks(
							"profiles",
							`
							id, full_name, email, school_id,
							schools!profiles_school_id_fkey (name)
						`,
							"id",
							teacherIds
						),
						fetchRowsByIdsInChunks(
							"course_categories",
							"id, name, grade",
							"id",
							categoryIds
						),
						fetchRowsByIdsInChunks(
							"course_contents",
							"id, title",
							"id",
							contentIds
						),
						supabase.from("grades").select("id, name"),
					]);

				if (gradesData.error) throw gradesData.error;

				// Create lookup maps
				const profilesMap = new Map(profilesRows.map((p) => [p.id, p]));
				const categoriesMap = new Map(categoriesRows.map((c) => [c.id, c]));
				const contentsMap = new Map(contentsRows.map((c) => [c.id, c]));
				const gradesMap = new Map(gradesData.data?.map((g) => [g.id, g]) || []);

				// Process the data
				const processedData = gameResultsData
					.map((result) => {
						const profile = profilesMap.get(result.teacher_id);
						const category = categoriesMap.get(result.category_id);
						const content = contentsMap.get(result.content_id);
						const grade = gradesMap.get(result.grade_id);

						return {
							...result,
							school_name: profile?.schools?.name || "Unknown School",
							school_id: profile?.school_id || null,
							teacher_name:
								profile?.full_name ||
								profile?.email?.split("@")[0] ||
								"Unknown Teacher",
							game_name: category?.name || "Unknown Game",
							content_name: getContentDisplayTitle(content?.title) || "Unknown Content",
							grade_name: grade?.name || "Unknown Grade",
							grade_id: category?.grade || null,
						};
					})
					.filter((result) => {
						// Apply school filtering for certain roles
						if (requiresSchoolFiltering) {
							return (
								schoolIds.length === 0 || schoolIds.includes(result.school_id)
							);
						}
						return true;
					});

				setGameResults(processedData);
				setFilteredResults(processedData);

				// Extract unique values for filters
				const uniqueSchools = [
					...new Set(processedData.map((r) => r.school_name)),
				]
					.filter(Boolean)
					.sort();
				const uniqueGrades = [
					...new Set(processedData.map((r) => r.grade_name)),
				]
					.filter(Boolean)
					.sort();
				const uniqueGames = [...new Set(processedData.map((r) => r.game_name))]
					.filter(Boolean)
					.sort();

				setSchools(uniqueSchools);
				setGrades(uniqueGrades);
				setGames(uniqueGames);
			} catch (error) {
				console.error("Error fetching game results:", error);
				setError("Failed to load game results. Please try again.");
			} finally {
				setLoading(false);
			}
		},
		[requiresSchoolFiltering, selectedYear]
	);

	// OPTIMIZED: Check user role effect
	useEffect(() => {
		if (user?.id) {
			checkUserRole();
		}
	}, [user?.id, checkUserRole]);

	// OPTIMIZED: Fetch user schools effect
	useEffect(() => {
		if (userProfile?.role && requiresSchoolFiltering) {
			fetchUserSchools();
		} else if (userProfile?.role) {
			fetchGameResults([]);
		}
	}, [
		userProfile?.role,
		requiresSchoolFiltering,
		fetchUserSchools,
		fetchGameResults,
	]);

	// OPTIMIZED: Fetch game results effect
	useEffect(() => {
		if (requiresSchoolFiltering) {
			if (userSchoolIds.length > 0) {
				fetchGameResults(userSchoolIds);
			} else if (userProfile) {
				setGameResults([]);
				setFilteredResults([]);
				setLoading(false);
			}
		}
	}, [userSchoolIds, userProfile, requiresSchoolFiltering, fetchGameResults]);

	// MEMOIZED: Apply filters function with pagination preservation
	const applyFilters = useCallback(
		(resetPage = true) => {
			let filtered = gameResults;

			if (searchQuery.trim()) {
				const query = searchQuery.toLowerCase();
				filtered = filtered.filter(
					(r) =>
						r.school_name.toLowerCase().includes(query) ||
						r.teacher_name.toLowerCase().includes(query) ||
						r.grade_name.toLowerCase().includes(query) ||
						r.game_name.toLowerCase().includes(query) ||
						r.content_name.toLowerCase().includes(query) ||
						(r.original_filename &&
							r.original_filename.toLowerCase().includes(query)) ||
						new Date(r.created_at).toLocaleDateString().includes(query)
				);
			}

			if (schoolFilter) {
				filtered = filtered.filter((r) => r.school_name === schoolFilter);
			}
			if (gradeFilter) {
				filtered = filtered.filter((r) => r.grade_name === gradeFilter);
			}
			if (gameFilter) {
				filtered = filtered.filter((r) => r.game_name === gameFilter);
			}
			if (editedFilter !== "all") {
				if (editedFilter === "edited") {
					filtered = filtered.filter((r) => r.has_edited_file);
				} else if (editedFilter === "not_edited") {
					filtered = filtered.filter((r) => !r.has_edited_file);
				}
			}

			setFilteredResults(filtered);

			// Only reset page when filters actually change, not on data updates
			if (resetPage) {
				setPage(0);
			}
		},
		[
			gameResults,
			searchQuery,
			schoolFilter,
			gradeFilter,
			gameFilter,
			editedFilter,
		]
	);

	// OPTIMIZED: Apply filters effect - separate handling for filter changes vs data updates
	useEffect(() => {
		applyFilters(true); // Reset page when filters change
	}, [searchQuery, schoolFilter, gradeFilter, gameFilter, editedFilter]);

	// Separate effect for data updates without resetting pagination
	useEffect(() => {
		applyFilters(false); // Don't reset page when only data changes
	}, [gameResults]);

	// MEMOIZED: Pagination handlers
	const handleChangePage = useCallback((event, newPage) => {
		setPage(newPage);
	}, []);

	const handleChangeRowsPerPage = useCallback((event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}, []);

	// MEMOIZED: Get paginated data
	const paginatedData = useMemo(() => {
		return filteredResults.slice(
			page * rowsPerPage,
			page * rowsPerPage + rowsPerPage
		);
	}, [filteredResults, page, rowsPerPage]);

	// MEMOIZED: Clear filters function
	const clearFilters = useCallback(() => {
		setSearchQuery("");
		setSchoolFilter(null);
		setGradeFilter(null);
		setGameFilter(null);
		setEditedFilter("all");
	}, []);

	// 🔧 FIXED: Update download count function - preserve pagination
	const updateDownloadCount = useCallback(
		async (resultId, fileType) => {
			try {
				const { data: currentData, error: fetchError } = await supabase
					.from("game_results")
					.select("download_count")
					.eq("id", resultId)
					.single();

				if (fetchError) {
					console.error("Error fetching current download count:", fetchError);
					return;
				}

				const newDownloadCount = (currentData.download_count || 0) + 1;

				const { error: updateError } = await supabase
					.from("game_results")
					.update({
						download_count: newDownloadCount,
						last_downloaded_at: new Date().toISOString(),
					})
					.eq("id", resultId);

				if (updateError) {
					console.error("Error updating download count:", updateError);
					return;
				}

				const { error: insertError } = await supabase
					.from("game_result_downloads")
					.insert({
						game_result_id: resultId,
						downloaded_by: user.id,
						file_type: fileType,
					});

				if (insertError) console.error("Error tracking download:", insertError);

				// 🔧 FIX: Update both gameResults AND filteredResults without triggering filter reset
				const updateResults = (results) =>
					results.map((result) =>
						result.id === resultId
							? {
									...result,
									download_count: newDownloadCount,
									last_downloaded_at: new Date().toISOString(),
							  }
							: result
					);

				// Update states without triggering applyFilters useEffect
				setGameResults((prev) => {
					const updated = updateResults(prev);
					// Directly update filteredResults as well to avoid the filter effect
					setFilteredResults((current) => updateResults(current));
					return updated;
				});
			} catch (error) {
				console.error("Error updating download statistics:", error);
			}
		},
		[user?.id]
	);

	// Updated CSV export
	const handleDownloadData = useCallback(() => {
		try {
			const headers = [
				"School",
				"Teacher",
				"Grade",
				"Game",
				"Content",
				"Original Filename",
				"Uploaded On",
				"Download Count",
				"Original File Type",
				"Original File Extension",
				"Has Edited File",
				"Edited File Type",
				"Edited File Extension",
				"Edit Date",
				"Teacher Downloads",
				"Teacher Downloaded Date",
				"File Path",
			];

			const csvData = filteredResults.map((result) => {
				const originalFile = getFileTypeAndExtension(result);
				const editedFile = getEditedFileTypeAndExtension(result);

				return [
					result.school_name,
					result.teacher_name,
					result.grade_name,
					result.game_name,
					result.content_name,
					result.original_filename || "",
					new Date(result.created_at).toLocaleDateString(),
					result.download_count || 0,
					originalFile.type,
					originalFile.extension,
					result.has_edited_file ? "Yes" : "No",
					editedFile.type !== "unknown" ? editedFile.type : "",
					editedFile.extension || "",
					result.edited_file_uploaded_at
						? new Date(result.edited_file_uploaded_at).toLocaleDateString()
						: "",
					result.teacher_download_count || 0,
					result.teacher_last_downloaded_at
						? new Date(result.teacher_last_downloaded_at).toLocaleDateString()
						: "",
					result.file_path || "",
				];
			});

			const csvContent = [
				headers.join(","),
				...csvData.map((row) =>
					row
						.map((field) =>
							typeof field === "string" && field.includes(",")
								? `"${field.replace(/"/g, '""')}"`
								: field
						)
						.join(",")
				),
			].join("\n");

			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `game_results_data_${
				new Date().toISOString().split("T")[0]
			}.csv`;
			document.body.appendChild(link);
			link.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(link);
		} catch (error) {
			console.error("Error downloading data:", error);
			setError("Failed to download data. Please try again.");
		}
	}, [filteredResults, getFileTypeAndExtension, getEditedFileTypeAndExtension]);

	// Open file in a new browser tab for preview
	const handleViewFile = useCallback(async (filePath, label = "file") => {
		if (!filePath) {
			setError(`No ${label} available to view.`);
			return;
		}

		try {
			const { data, error } = await supabase.storage
				.from("game-results")
				.createSignedUrl(filePath, 3600);

			if (error) throw error;

			if (data?.signedUrl) {
				window.open(data.signedUrl, "_blank", "noopener,noreferrer");
			}
		} catch (error) {
			console.error("Error viewing file:", error);
			setError(getStorageActionErrorMessage(error, "open file"));
		}
	}, []);

	const handleViewResult = useCallback(
		(result) => {
			if (result.has_edited_file && result.edited_file_path) {
				handleViewFile(result.edited_file_path, "edited file");
				return;
			}
			handleViewFile(result.file_path, "original file");
		},
		[handleViewFile]
	);

	// 🔧 FIXED: Download original file
	const handleDownloadOriginal = useCallback(
		async (result) => {
			try {
				const { data, error } = await supabase.storage
					.from("game-results")
					.download(result.file_path);

				if (error) throw error;

				const url = window.URL.createObjectURL(data);
				const link = document.createElement("a");
				link.href = url;
				link.download =
					result.generated_filename ||
					result.original_filename ||
					result.file_path.split("/").pop();
				document.body.appendChild(link);
				link.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(link);

				await updateDownloadCount(result.id, "original");
			} catch (error) {
				console.error("Error downloading file:", error);
				setError(getStorageActionErrorMessage(error, "download file"));
			}
		},
		[updateDownloadCount]
	); // Removed gameResults and filteredResults from dependencies

	// 🔧 FIXED: Download edited file
	const handleDownloadEdited = useCallback(
		async (result) => {
			if (!result.edited_file_path) {
				setError("No edited file available.");
				return;
			}

			try {
				const { data, error } = await supabase.storage
					.from("game-results")
					.download(result.edited_file_path);

				if (error) throw error;

				const url = window.URL.createObjectURL(data);
				const link = document.createElement("a");
				link.href = url;
				link.download =
					`edited_${result.generated_filename || result.original_filename}` ||
					result.edited_file_path.split("/").pop();
				document.body.appendChild(link);
				link.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(link);

				await updateDownloadCount(result.id, "edited");
			} catch (error) {
				console.error("Error downloading edited file:", error);
				setError(getStorageActionErrorMessage(error, "download edited file"));
			}
		},
		[updateDownloadCount]
	);

	// 🔧 FIXED: Handle edit file upload - preserve pagination
	const handleEditUpload = useCallback(async () => {
		if (!editFile || !selectedResult) return;

		setUploadingEdit(true);
		try {
			const timestamp = new Date().toISOString().split("T")[0];
			const fileExt = editFile.name.split(".").pop().toLowerCase();

			// 🔧 FIX: Include grade name in the file path to make it unique
			// Clean the grade name to remove any special characters that might cause issues
			const cleanGradeName = selectedResult.grade_name.replace(
				/[^a-zA-Z0-9]/g,
				"_"
			);
			const cleanGameName = selectedResult.game_name.replace(
				/[^a-zA-Z0-9]/g,
				"_"
			);
			const cleanContentName = selectedResult.content_name.replace(
				/[^a-zA-Z0-9]/g,
				"_"
			);

			const editedFilePath = `${selectedResult.user_folder}/edited_${cleanGameName}_${cleanContentName}_${cleanGradeName}_${timestamp}.${fileExt}`;

			const { data: storageData, error: storageError } = await supabase.storage
				.from("game-results")
				.upload(editedFilePath, editFile, {
					cacheControl: "3600",
					upsert: true, // This will now work correctly with unique filenames
					contentType: editFile.type,
				});

			if (storageError) throw storageError;

			const { data: urlData, error: urlError } = await supabase.storage
				.from("game-results")
				.createSignedUrl(editedFilePath, 3600);

			if (urlError) throw urlError;

			const editedFileType = editFile.type.startsWith("image/")
				? "image"
				: "video";
			const editedFileTypeWithExt = `${editedFileType}: ${fileExt}`;

			const { error: updateError } = await supabase
				.from("game_results")
				.update({
					edited_file_path: editedFilePath,
					edited_file_url: urlData.signedUrl,
					edited_file_uploaded_at: new Date().toISOString(),
					edited_by: user.id,
					has_edited_file: true,
					edited_file_type: editedFileTypeWithExt,
				})
				.eq("id", selectedResult.id);

			if (updateError) throw updateError;

			const notifyResult = await sendEditedVideoNotification(supabase, {
				senderId: user.id,
				recipientId: selectedResult.teacher_id,
				gameName: selectedResult.game_name,
				contentName: selectedResult.content_name,
				gradeName: selectedResult.grade_name,
				gameResultId: selectedResult.id,
				academicYear: selectedResult.academic_year,
				currentAcademicYear,
			});

			if (!notifyResult.ok) {
				console.error(
					"Error sending edited video notification:",
					notifyResult.error
				);
			}

			// Update both states consistently without triggering pagination reset
			const updateData = {
				edited_file_path: editedFilePath,
				edited_file_url: urlData.signedUrl,
				edited_file_uploaded_at: new Date().toISOString(),
				has_edited_file: true,
				edited_file_type: editedFileTypeWithExt,
			};

			const updateResults = (results) =>
				results.map((result) =>
					result.id === selectedResult.id
						? { ...result, ...updateData }
						: result
				);

			// Update states without triggering applyFilters useEffect
			setGameResults((prev) => {
				const updated = updateResults(prev);
				// Directly update filteredResults as well to avoid the filter effect
				setFilteredResults((current) => updateResults(current));
				return updated;
			});

			setUploadDialog(false);
			setSelectedResult(null);
			setEditFile(null);

			setError(
				"Edited file uploaded successfully and teacher has been notified!"
			);
		} catch (error) {
			console.error("Error uploading edited file:", error);
			setError("Failed to upload edited file. Please try again.");
		} finally {
			setUploadingEdit(false);
		}
	}, [editFile, selectedResult, user?.id]);

	// Loading state
	if (loading) {
		return (
			<Box sx={{ p: 3 }}>
				<Skeleton variant="text" height={40} width="40%" sx={{ mb: 3 }} />
				<Paper sx={{ p: 3 }}>
					<Skeleton variant="rectangular" height={400} />
				</Paper>
			</Box>
		);
	}

	// Access denied only if admin/employee roles have no assigned schools
	if (requiresSchoolFiltering && userSchoolIds.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography
					variant="h5"
					sx={{ color: "#0f172a", fontWeight: 600, mb: 1 }}>
					Game Results Edits
				</Typography>
				<Alert severity="info" sx={{ mt: 2 }}>
					No schools are currently assigned to your account. Please contact your
					administrator to assign schools to view game results.
				</Alert>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, minHeight: "100vh" }}>
			<Box sx={{ maxWidth: "1600px", mx: "auto" }}>
				{/* Header */}
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="flex-start"
					sx={{ mb: 4 }}>
					<Box>
						<Typography
							variant="h5"
							sx={{ color: "#0f172a", fontWeight: 600, mb: 1 }}>
							Game Results Edits
						</Typography>
						<Typography variant="body2" sx={{ color: "#64748b" }}>
							Manage and edit uploaded game result files from all teachers
							{requiresSchoolFiltering && (
								<span> in your assigned schools</span>
							)}
						</Typography>
					</Box>
					<Stack direction="row" spacing={2} alignItems="center">
						<AcademicYearSelector
							value={selectedYear}
							onChange={setSelectedYear}
						/>
						<Button
							variant="outlined"
							startIcon={<Download size={16} />}
							onClick={handleDownloadData}
							sx={{ height: "40px" }}>
							Export Data
						</Button>
						<Button
							variant="outlined"
							startIcon={
								showFilters ? (
									<ChevronUp size={16} />
								) : (
									<ChevronDown size={16} />
								)
							}
							onClick={() => setShowFilters(!showFilters)}
							sx={{ height: "40px" }}>
							{showFilters ? "Hide" : "Show"} Filters
						</Button>
					</Stack>
				</Stack>

				{/* Search and Filters */}
				<Collapse in={showFilters}>
					<Paper
						sx={{
							p: 3,
							mb: 3,
							borderRadius: 2,
							boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
						}}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
							Search & Filters
						</Typography>

						{/* Search Box */}
						<Box sx={{ mb: 3 }}>
							<TextField
								fullWidth
								size="small"
								placeholder="Search across all fields (school, teacher, grade, game, content, filename...)"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								InputProps={{
									startAdornment: (
										<Search
											size={16}
											style={{ marginRight: 8, color: "#64748b" }}
										/>
									),
								}}
							/>
						</Box>

						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12} sm={6} md={2.4}>
								<Autocomplete
									size="small"
									options={schools}
									value={schoolFilter}
									onChange={(event, newValue) => {
										setSchoolFilter(newValue);
									}}
									renderInput={(params) => (
										<TextField {...params} label="School" />
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={2.4}>
								<Autocomplete
									size="small"
									options={grades}
									value={gradeFilter}
									onChange={(event, newValue) => {
										setGradeFilter(newValue);
									}}
									renderInput={(params) => (
										<TextField {...params} label="Grade" />
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={2.4}>
								<Autocomplete
									size="small"
									options={games}
									value={gameFilter}
									onChange={(event, newValue) => {
										setGameFilter(newValue);
									}}
									renderInput={(params) => (
										<TextField {...params} label="Game" />
									)}
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={2.4}>
								<FormControl fullWidth size="small">
									<InputLabel>Edit Status</InputLabel>
									<Select
										value={editedFilter}
										label="Edit Status"
										onChange={(e) => setEditedFilter(e.target.value)}>
										<MenuItem value="all">All Files</MenuItem>
										<MenuItem value="edited">Has Edited File</MenuItem>
										<MenuItem value="not_edited">No Edited File</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6} md={2.4}>
								<Button
									variant="outlined"
									onClick={clearFilters}
									fullWidth
									sx={{ height: "40px" }}>
									Clear All
								</Button>
							</Grid>
						</Grid>
					</Paper>
				</Collapse>

				{/* Results Summary */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="body2" sx={{ color: "#64748b" }}>
						Showing {filteredResults.length} of {gameResults.length} results
						{searchQuery.trim() && <span> for "{searchQuery}"</span>}
						{requiresSchoolFiltering && userSchoolIds.length > 0 && (
							<span> from your assigned schools</span>
						)}
					</Typography>
				</Box>

				{/* Error Alert */}
				{error && (
					<Alert
						severity={error.includes("successfully") ? "success" : "error"}
						sx={{ mb: 3 }}
						onClose={() => setError("")}>
						{error}
					</Alert>
				)}

				{/* Results Table */}
				<Paper sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>School</TableCell>
									<TableCell>Teacher</TableCell>
									<TableCell>Grade</TableCell>
									<TableCell>Game</TableCell>
									<TableCell>Content</TableCell>
									<TableCell>Uploaded On</TableCell>
									<TableCell>Downloads</TableCell>
									<TableCell>Original File Type</TableCell>
									<TableCell>Edited File</TableCell>
									<TableCell>Edited File Type</TableCell>
									<TableCell>Edit Date</TableCell>
									<TableCell>Teacher Downloads</TableCell>
									<TableCell>Teacher Downloaded Date</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedData.map((result) => {
									const originalFile = getFileTypeAndExtension(result);
									const editedFile = getEditedFileTypeAndExtension(result);

									return (
										<TableRow key={result.id} hover>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<School size={16} color="#64748b" />
													<Typography variant="body2">
														{result.school_name}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<User size={16} color="#64748b" />
													<Typography variant="body2">
														{result.teacher_name}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Chip
													size="small"
													label={result.grade_name}
													sx={{ bgcolor: "#f1f5f9", color: "#475569" }}
												/>
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<GamepadIcon size={16} color="#64748b" />
													<Typography variant="body2">
														{result.game_name}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{result.content_name}
												</Typography>
											</TableCell>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<Calendar size={16} color="#64748b" />
													<Typography variant="body2">
														{new Date(result.created_at).toLocaleDateString()}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Chip
													size="small"
													label={`${result.download_count || 0} times`}
													sx={{ bgcolor: "#dbeafe", color: "#1e40af" }}
												/>
											</TableCell>
											{/* Original File Type Column */}
											<TableCell>
												<Chip
													size="small"
													icon={
														originalFile.type === "image" ? (
															<Image size={14} />
														) : (
															<Video size={14} />
														)
													}
													label={`${originalFile.type}: ${originalFile.extension}`}
													sx={{
														bgcolor:
															originalFile.type === "image"
																? "#f0fdf4"
																: "#eff6ff",
														color:
															originalFile.type === "image"
																? "#059669"
																: "#3b82f6",
														"& .MuiChip-icon": { color: "inherit" },
													}}
												/>
											</TableCell>
											<TableCell>
												{result.has_edited_file ? (
													<Chip
														size="small"
														label="Yes"
														sx={{ bgcolor: "#dcfce7", color: "#166534" }}
													/>
												) : (
													<Chip
														size="small"
														label="No"
														sx={{ bgcolor: "#fee2e2", color: "#dc2626" }}
													/>
												)}
											</TableCell>
											{/* Edited File Type Column */}
											<TableCell>
												{result.has_edited_file &&
												editedFile.type !== "unknown" ? (
													<Chip
														size="small"
														icon={
															editedFile.type === "image" ? (
																<Image size={14} />
															) : (
																<Video size={14} />
															)
														}
														label={`${editedFile.type}: ${editedFile.extension}`}
														sx={{
															bgcolor:
																editedFile.type === "image"
																	? "#fef3c7"
																	: "#ddd6fe",
															color:
																editedFile.type === "image"
																	? "#92400e"
																	: "#5b21b6",
															"& .MuiChip-icon": { color: "inherit" },
														}}
													/>
												) : (
													<Typography variant="body2" sx={{ color: "#9ca3af" }}>
														-
													</Typography>
												)}
											</TableCell>
											<TableCell>
												{result.edited_file_uploaded_at ? (
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<Calendar size={16} color="#64748b" />
														<Typography variant="body2">
															{new Date(
																result.edited_file_uploaded_at
															).toLocaleDateString()}
														</Typography>
													</Stack>
												) : (
													<Typography variant="body2" sx={{ color: "#9ca3af" }}>
														-
													</Typography>
												)}
											</TableCell>
											{/* Teacher Downloads Column */}
											<TableCell>
												<Chip
													size="small"
													label={`${result.teacher_download_count || 0} times`}
													sx={{
														bgcolor:
															result.teacher_download_count > 0
																? "#f0fdf4"
																: "#f8fafc",
														color:
															result.teacher_download_count > 0
																? "#166534"
																: "#64748b",
													}}
												/>
											</TableCell>
											{/* Teacher Downloaded Date Column */}
											<TableCell>
												{result.teacher_last_downloaded_at ? (
													<Stack
														direction="row"
														spacing={1}
														alignItems="center">
														<Calendar size={16} color="#64748b" />
														<Typography variant="body2">
															{new Date(
																result.teacher_last_downloaded_at
															).toLocaleDateString()}
														</Typography>
													</Stack>
												) : (
													<Typography variant="body2" sx={{ color: "#9ca3af" }}>
														-
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">
												<Stack
													direction="row"
													spacing={1}
													justifyContent="flex-end">
													<IconButton
														size="small"
														onClick={() => handleViewResult(result)}
														title={
															result.has_edited_file
																? "View edited video"
																: "View original video"
														}
														sx={{ color: "#6366f1" }}>
														<Eye size={16} />
													</IconButton>

													<IconButton
														size="small"
														onClick={() => handleDownloadOriginal(result)}
														title="Download Original"
														sx={{ color: "#3b82f6" }}>
														<Download size={16} />
													</IconButton>

													<IconButton
														size="small"
														onClick={() => {
															setSelectedResult(result);
															setUploadDialog(true);
														}}
														title={
															(result.download_count || 0) === 0
																? "File must be downloaded at least once before editing"
																: "Upload Edited File"
														}
														disabled={(result.download_count || 0) === 0}
														sx={{
															color:
																(result.download_count || 0) === 0
																	? "#9ca3af"
																	: "#10b981",
															"&.Mui-disabled": {
																color: "#9ca3af",
															},
														}}>
														<Upload size={16} />
													</IconButton>

													{result.has_edited_file && (
														<IconButton
															size="small"
															onClick={() => handleDownloadEdited(result)}
															title="Download Edited File"
															sx={{ color: "#8b5cf6" }}>
															<Edit size={16} />
														</IconButton>
													)}

													{canDelete && (
														<IconButton
															size="small"
															onClick={() => {
																setResultToDelete(result);
																setDeleteDialog(true);
															}}
															title="Delete Game Result"
															sx={{
																color: "#dc2626",
																"&:hover": {
																	bgcolor: "#fef2f2",
																},
															}}>
															<Trash size={16} />
														</IconButton>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
								{paginatedData.length === 0 && (
									<TableRow>
										<TableCell colSpan={14} align="center" sx={{ py: 4 }}>
											<Typography variant="body2" sx={{ color: "#64748b" }}>
												{gameResults.length === 0
													? requiresSchoolFiltering
														? "No game results found in your assigned schools"
														: "No game results found"
													: "No game results found matching the selected filters"}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={filteredResults.length}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 25, 50, 100]}
						sx={{
							borderTop: "1px solid #e2e8f0",
							".MuiTablePagination-select": { py: 0.5 },
						}}
					/>
				</Paper>

				{/* Upload Edited File Dialog */}
				<Dialog
					open={uploadDialog}
					onClose={() => setUploadDialog(false)}
					maxWidth="sm"
					fullWidth>
					<DialogTitle>Upload Edited File</DialogTitle>
					<DialogContent>
						<Box sx={{ mb: 2 }}>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
								For: <strong>{selectedResult?.teacher_name}</strong> -{" "}
								{selectedResult?.game_name} ({selectedResult?.grade_name})
							</Typography>
							<input
								type="file"
								accept="image/*,video/*"
								onChange={(e) => setEditFile(e.target.files[0])}
								style={{ width: "100%" }}
							/>
						</Box>
						{editFile && (
							<Alert severity="info" sx={{ mt: 2 }}>
								Selected: {editFile.name} (
								{(editFile.size / 1024 / 1024).toFixed(2)} MB)
							</Alert>
						)}
						<Alert severity="info" sx={{ mt: 2 }}>
							📧 The teacher will be automatically notified when the edited file
							is uploaded.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => setUploadDialog(false)}
							disabled={uploadingEdit}>
							Cancel
						</Button>
						<Button
							onClick={handleEditUpload}
							disabled={!editFile || uploadingEdit}
							variant="contained">
							{uploadingEdit ? "Uploading..." : "Upload & Notify Teacher"}
						</Button>
					</DialogActions>
				</Dialog>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={deleteDialog}
					onClose={() => setDeleteDialog(false)}
					maxWidth="sm"
					fullWidth>
					<DialogTitle sx={{ color: "#dc2626", fontWeight: 600 }}>
						Delete Game Result
					</DialogTitle>
					<DialogContent>
						<Alert severity="warning" sx={{ mb: 2 }}>
							<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
								⚠️ This action cannot be undone!
							</Typography>
							<Typography variant="body2">
								This will permanently delete all files and data associated with
								this game result.
							</Typography>
						</Alert>

						<Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 1 }}>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>School:</strong> {resultToDelete?.school_name}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Teacher:</strong> {resultToDelete?.teacher_name}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Game:</strong> {resultToDelete?.game_name} (
								{resultToDelete?.grade_name})
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
								<strong>Content:</strong> {resultToDelete?.content_name}
							</Typography>
							<Typography variant="body2" sx={{ color: "#64748b" }}>
								<strong>Uploaded:</strong>{" "}
								{resultToDelete &&
									new Date(resultToDelete.created_at).toLocaleDateString()}
							</Typography>
						</Box>

						<Typography
							variant="body2"
							sx={{ color: "#dc2626", mt: 2, fontWeight: 500 }}>
							Files to be deleted:
						</Typography>
						<ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
							<li>
								<Typography variant="body2" sx={{ color: "#64748b" }}>
									Original file:{" "}
									{resultToDelete?.original_filename || "Unknown filename"}
								</Typography>
							</li>
							{resultToDelete?.has_edited_file && (
								<li>
									<Typography variant="body2" sx={{ color: "#64748b" }}>
										Edited file: edited_
										{resultToDelete?.original_filename || "Unknown filename"}
									</Typography>
								</li>
							)}
							<li>
								<Typography variant="body2" sx={{ color: "#64748b" }}>
									All download tracking records
								</Typography>
							</li>
						</ul>
					</DialogContent>
					<DialogActions sx={{ p: 3, pt: 1 }}>
						<Button
							onClick={() => setDeleteDialog(false)}
							disabled={deletingResult}
							variant="outlined"
							sx={{
								color: "#64748b",
								borderColor: "#e2e8f0",
								"&:hover": {
									borderColor: "#cbd5e1",
									bgcolor: "#f8fafc",
								},
							}}>
							Cancel
						</Button>
						<Button
							onClick={handleDeleteResult}
							disabled={deletingResult}
							variant="contained"
							sx={{
								bgcolor: "#dc2626",
								"&:hover": {
									bgcolor: "#b91c1c",
								},
								"&.Mui-disabled": {
									bgcolor: "#9ca3af",
								},
							}}>
							{deletingResult ? "Deleting..." : "Delete Permanently"}
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</Box>
	);
};

export default GameResultsEdits;
